import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from 'axios';

/**
 * Handles accepting a task assignment
 */
export const createTaskAcceptHandler = (
  updateTask,
  createActivity,
  updateWorkflowStep,
  user,
  t,
  loadTasks,
  setTaskDetailOpen
) => {
  return async (task) => {
    try {
      console.log("Starting task accept process", { taskId: task.id, userId: user?.id });

      // Call dedicated backend endpoint to mark task as accepted
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/accept`,
        null,
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      );

      // Let the backend create activity & notifications. No need for duplicate frontend activity.

      toast.success(t('workflows:task_accepted', 'Task accepted successfully'));
      // Refresh the tasks list to update the UI with the new task status
      await loadTasks();
      setTaskDetailOpen(false);
    } catch (err) {
      console.error("Task accept error:", err);
      toast.error(err.response?.data?.message || t('workflows:task_accept_error', 'Failed to accept task'));
    }
  };
};

/**
 * Handles rejecting a task assignment
 */
export const createTaskRejectHandler = (
  updateTask,
  createActivity,
  updateWorkflowStep,
  user,
  t,
  loadTasks,
  setRejectDialogOpen
) => {
  return async (selectedTask, reason) => {
    try {
      // Update the task assignment
      await updateTask(selectedTask.id, { 
        status: 'REJECTED', 
        notes: reason,
        completed_at: new Date().toISOString()
      });
      
      // Create document activity
      await createActivity({
        document_id: selectedTask.document_id,
        user_id: user.id,
        activity_type: 'REJECTED',
        description: `${user.full_name || user.username} دا سند رد شو د: ${selectedTask.workflowStep?.name }لخوا`,
        step_id: selectedTask.workflow_step_id,
        metadata: {
          previous_status: 'IN_PROGRESS',
          new_status: 'REJECTED',
          task_id: selectedTask.id,
          rejection_reason: reason
        }
      });
      
      // Update workflow step status if needed
      if (selectedTask.workflow_step_id) {
        await updateWorkflowStep(selectedTask.workflow_step_id, {
          status: 'REJECTED'
        });
      }
      
      toast.success(t('workflows:task_rejected', 'Task rejected successfully'));
      // Ensure UI is updated to show task in the rejected tab
      await loadTasks();
      setRejectDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || t('workflows:task_reject_error', 'Failed to reject task'));
      throw err; // Re-throw to be caught by dialog
    }
  };
};

/**
 * Handles approving a task assignment
 */
export const createTaskApproveHandler = (
  updateTask,
  createActivity,
  updateWorkflowStep,
  updateWorkflow,
  user,
  t,
  loadTasks,
  setApproveDialogOpen
) => {
  return async (selectedTask, remarks) => {
    try {
      // Call dedicated backend endpoint to complete the task. This will create
      // both activity log and notifications server-side, ensuring consistency.
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${selectedTask.id}/complete`,
        { notes: remarks || '' },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      // Update workflow step status if needed
      if (selectedTask.workflow_step_id) {
        await updateWorkflowStep(selectedTask.workflow_step_id, {
          status: 'COMPLETED'
        });
        
        // Check if this was the last step in the workflow to potentially complete the workflow
        const workflowId = selectedTask.workflowStep?.workflow_id;
        if (workflowId) {
          // This would need a backend API that checks if all steps are completed
          // For now, we'll just mark the step as completed
          // In a real implementation, you'd check if this was the last step and update the workflow accordingly
          try {
            await updateWorkflow(workflowId, {
              status: 'completed'
            });
            
            // Add a workflow completed activity
            await createActivity({
              document_id: selectedTask.document_id,
              user_id: user.id,
              activity_type: 'WORKFLOW_COMPLETED',
              description: `Workflow completed for document`,
              metadata: {
                workflow_id: workflowId
              }
            });
          } catch (error) {
            console.error('Error updating workflow:', error);
          }
        }
      }
      
      toast.success(t('workflows:task_approved', 'Task completed successfully'));
      // Ensure UI is updated to show task in Completed tab
      await loadTasks();
      setApproveDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || t('workflows:task_approve_error', 'Failed to complete task'));
      throw err; // Re-throw to be caught by dialog
    }
  };
};

/**
 * Helper function for safe date formatting
 */
export const safeFormatDate = (dateString, formatString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '-';
    }
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return '-';
  }
};

/**
 * Helper to determine if a task should be considered "in progress"
 */
export const isTaskInProgress = (task) => {
  return task.status === 'IN_PROGRESS';
}; 